import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

import axios from 'axios'
import { ACCESS_TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY } from '~/constants'
import storage from '~/foundation/storage/localstorage'
import { redirect } from './keycloack-api.redirect'
import { refreshTokens } from './keycloack-api.refresh'

/**
 * Interceptor to inject the access token into the request.
 *
 * @param config - The request config.
 * @returns The new request config.
 */
export function injectAccessTokenInterceptor(config: InternalAxiosRequestConfig) {
  const accessToken = storage.get<string>(ACCESS_TOKEN_STORAGE_KEY)
  if (accessToken && config.headers)
    config.headers.Authorization = `Bearer ${accessToken}`

  return config
}

// Guards against every request that shares a failed refresh triggering its
// own redirect once the user is sent back to the Keycloak login page.
let hasRedirectedToLogin = false

// Breaks the redirect -> Keycloak SSO -> back -> 401 -> redirect cycle when
// the 401 isn't actually caused by an expired session (e.g. misconfiguration,
// a backend outage). Tracked in sessionStorage because each loop iteration is
// a full page reload, which resets every module-level variable above.
const REDIRECT_ATTEMPTS_KEY = 'kc_login_redirect_attempts'
const REDIRECT_ATTEMPTS_WINDOW_MS = 30_000
const MAX_REDIRECT_ATTEMPTS = 2

function shouldRedirectToLogin() {
  const now = Date.now()
  const raw = sessionStorage.getItem(REDIRECT_ATTEMPTS_KEY)
  const state = raw ? JSON.parse(raw) as { count: number, firstAttemptAt: number } : null

  const isWithinWindow = state && now - state.firstAttemptAt < REDIRECT_ATTEMPTS_WINDOW_MS
  const nextState = isWithinWindow
    ? { count: state.count + 1, firstAttemptAt: state.firstAttemptAt }
    : { count: 1, firstAttemptAt: now }

  sessionStorage.setItem(REDIRECT_ATTEMPTS_KEY, JSON.stringify(nextState))

  return nextState.count <= MAX_REDIRECT_ATTEMPTS
}

function recoverFromUnrecoverable401() {
  storage.remove(ACCESS_TOKEN_STORAGE_KEY)
  storage.remove(REFRESH_TOKEN_STORAGE_KEY)

  if (hasRedirectedToLogin)
    return

  if (!shouldRedirectToLogin())
    return

  hasRedirectedToLogin = true
  redirect()
}

/**
 * Interceptor to handle expired tokens.
 *
 * @param error - The error.
 * @returns The new request.
 */
export async function refreshTokenResponseInterceptor(error: AxiosError) {
  const originalRequest = error.config as { _retry?: boolean } & AxiosRequestConfig
  if (!originalRequest)
    return Promise.reject(error)

  if (error.response && error.response.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true

    const refreshToken = storage.get<string>(REFRESH_TOKEN_STORAGE_KEY)
    if (!refreshToken) {
      recoverFromUnrecoverable401()
      return Promise.reject(error)
    }

    try {
      const tokens = await refreshTokens(refreshToken)

      // update original request header with new token
      if (originalRequest.headers)
        originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`

      return axios(originalRequest)
    }
    catch (refreshError) {
      // if refresh token is invalid then remove tokens and send the user back to login
      recoverFromUnrecoverable401()
      return Promise.reject(refreshError)
    }
  }

  return Promise.reject(error)
}
